/**
 * Wires the three generated season hooks into `PoolSeasonCard` and
 * `StartSeasonDialog`, runs the mutation, invalidates the reads that
 * change, and navigates on success and hands off to the New contest dialog
 * (ISSUE-1042). Fills the `season` slot reserved by
 * `poolSettingsState.ts`.
 *
 * `useSeasonStart`'s generated `invalidateRelated` only covers
 * `/season/list`. This container invalidates `/season/start-options` and
 * `/contest/list` by hand too, on a successful start, mirroring
 * `ReviveSheetContainer.tsx`'s stated reason: a generated mutation hook's
 * own invalidation cannot be assumed to cover every affected read.
 */
import {
  useSeasonList,
  useSeasonStart,
  useSeasonStartOptions,
} from "@sportsmet/contest-sdk/private";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type JSX, useEffect, useRef, useState } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { closeOverlay, type typeOverlaySearch } from "../../routeState/overlay";
import { useOverlay } from "../../routeState/useOverlay";
import { NEW_CONTEST_DIALOG_NAME } from "../newContest/newContestModel";
import { openPoolTabSearch } from "../poolScreen/poolScreenRoute";
import { PastSeasonsDialogContainer } from "./PastSeasonsDialogContainer";
import { listPastSeasons, PAST_SEASONS_DIALOG_NAME } from "./pastSeasonsModel";
import { PoolSeasonCard } from "./PoolSeasonCard";
import {
  buildSeasonStartPayload,
  currentSeasonLabel,
  resolveSeasonGate,
  startFailureNote,
} from "./seasonCardModel";
import { StartSeasonDialog } from "./StartSeasonDialog";

export const START_SEASON_DIALOG_NAME = "start-season";

// The three query key prefixes this container invalidates by hand after a
// successful start, matching the endpoint suffixes /season/list,
// /season/start-options and /contest/list.
const SEASON_RELATED_QUERY_KEY_PREFIXES = [
  "post:season:seasonList",
  "post:season:seasonStartOptions",
  "post:contest:contestList",
];

export type typePoolSeasonCardContainerProps = {
  poolId: string;
};

export function PoolSeasonCardContainer(
  props: typePoolSeasonCardContainerProps,
): JSX.Element {
  const { poolId } = props;

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const overlay = useOverlay();

  const list = useSeasonList({ poolId });
  const options = useSeasonStartOptions({ poolId });
  const start = useSeasonStart();

  useEffect(() => {
    const code = extractErrorCode(list.data);
    if (code !== null) showApiError(list.data, undefined, list.dataUpdatedAt);
  }, [list.dataUpdatedAt, poolId]);
  useEffect(() => {
    const code = extractErrorCode(options.data);
    if (code !== null)
      showApiError(options.data, undefined, options.dataUpdatedAt);
  }, [options.dataUpdatedAt, poolId]);

  const isLoading = list.isLoading || options.isLoading;
  const pastSeasonCount = listPastSeasons(list.data?.data?.items ?? []).length;
  const nextSeasonYearDefault = options.data?.data?.nextSeasonYearDefault;
  const nextSeasonTitleDefault = options.data?.data?.nextSeasonTitleDefault;

  const gate = resolveSeasonGate({
    providerDataReady: isLoading
      ? false
      : (options.data?.data?.providerDataReady ?? false),
    unsettledContest: options.data?.data?.unsettledContest ?? null,
  });

  const [step, setStep] = useState<"pick" | "confirm">("pick");
  const [year, setYear] = useState<number>(nextSeasonYearDefault ?? 0);
  const [title, setTitle] = useState<string>(nextSeasonTitleDefault ?? "");
  const [inlineNote, setInlineNote] = useState<string | null>(null);

  const seededForRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      nextSeasonYearDefault === undefined ||
      nextSeasonTitleDefault === undefined
    ) {
      return;
    }
    const key = `${nextSeasonYearDefault}:${nextSeasonTitleDefault}`;
    if (seededForRef.current === key) return;
    seededForRef.current = key;
    setYear(nextSeasonYearDefault);
    setTitle(nextSeasonTitleDefault);
  }, [nextSeasonYearDefault, nextSeasonTitleDefault]);

  const isOpen = overlay.isOpen("dialog", START_SEASON_DIALOG_NAME);

  function handleOpenChange(open: boolean): void {
    if (open) {
      overlay.open({ kind: "dialog", name: START_SEASON_DIALOG_NAME });
      return;
    }
    overlay.close({ kind: "dialog" });
    setStep("pick");
    setInlineNote(null);
  }

  function goToGameTab(contestId: string): void {
    // ISSUE-1036. The contest and its confirm card live on this pool's Game
    // tab, so "Go to contest" and a successful start both switch to that tab
    // (a successful start also opens New contest, see below).
    // `openPoolTabSearch` drops `panel`, the Settings tab's own sub-address.
    // `closeOverlay` drops `dialog` in the same navigation: that closes the
    // start-season dialog, and it never forces `dialog=contest-confirm` in
    // (ISSUE-501 D4). The confirm card renders whether or not its dialog is
    // open, so it is in view even after "Not yet". `replace: true` matches
    // every other tab switch on the pool screen, so Back leaves the pool
    // rather than walking back through the tabs.
    void navigate({
      to: ".",
      replace: true,
      search: (previous: unknown) =>
        closeOverlay(
          {
            ...openPoolTabSearch(
              (previous ?? {}) as typeOverlaySearch & Record<string, unknown>,
              "game",
            ),
            contestId,
            roundId: undefined,
            entryId: undefined,
          },
          { kind: "dialog" },
        ),
    });
  }

  function goToGameTabWithNewContestForm(): void {
    // ISSUE-1042 R5 over ISSUE-1036: one replacing navigation leaves
    // Settings for this pool's Game tab (where the contest lives) and swaps
    // dialog=start-season for dialog=new-contest, so Back does not reopen the
    // season dialog. `openPoolTabSearch` drops `panel`, the Settings tab's own
    // sub-address.
    void navigate({
      to: ".",
      replace: true,
      search: (previous: unknown) => ({
        ...openPoolTabSearch(
          (previous ?? {}) as Record<string, unknown>,
          "game",
        ),
        dialog: NEW_CONTEST_DIALOG_NAME,
      }),
    });
  }

  async function handleConfirm(): Promise<void> {
    const result = await start.mutateAsync(
      buildSeasonStartPayload({ poolId, seasonYear: year, seasonTitle: title }),
    );

    if (result.error) {
      const note = startFailureNote(extractErrorCode(result));
      if (note !== null) {
        setInlineNote(note);
        return;
      }
      showApiError(result);
      return;
    }

    await queryClient.invalidateQueries({
      predicate: (query) => {
        const first = query.queryKey[0];
        return (
          typeof first === "string" &&
          SEASON_RELATED_QUERY_KEY_PREFIXES.includes(first)
        );
      },
    });
    setStep("pick");
    setInlineNote(null);
    goToGameTabWithNewContestForm();
  }

  return (
    <>
      <PoolSeasonCard
        isLoading={isLoading}
        currentSeasonLabel={currentSeasonLabel(list.data?.data?.items ?? [])}
        gate={gate}
        onStart={() => {
          handleOpenChange(true);
        }}
        onGoToContest={(contestId) => {
          // Select the target explicitly even when the viewer was browsing history.
          goToGameTab(contestId);
        }}
        pastSeasonCount={pastSeasonCount}
        onOpenPastSeasons={() => {
          overlay.open({ kind: "dialog", name: PAST_SEASONS_DIALOG_NAME });
        }}
      />
      <StartSeasonDialog
        open={isOpen}
        onOpenChange={handleOpenChange}
        step={step}
        year={year}
        minYear={nextSeasonYearDefault ?? year}
        maxYear={nextSeasonYearDefault ?? year}
        onYearChange={setYear}
        title={title}
        onTitleChange={setTitle}
        isSubmitting={start.isPending}
        inlineNote={inlineNote}
        onContinue={() => {
          setStep("confirm");
        }}
        onBackToPick={() => {
          setStep("pick");
          setInlineNote(null);
        }}
        onConfirm={() => {
          void handleConfirm();
        }}
      />
      <PastSeasonsDialogContainer poolId={poolId} />
    </>
  );
}
